import { useTranslation } from "react-i18next";
import { usePageSEO } from "@/lib/use-page-seo";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MegaFooter from "@/components/MegaFooter";
import { PLATFORM_ROUTES } from "../../../shared/seo-routes";

interface Props {
  params: { platformSlug?: string };
}

const STEP_KEYS = ["1", "2", "3", "4"] as const;
const FAQ_KEYS = ["1", "2", "3", "4", "5", "6"] as const;

export default function PlatformPage({ params }: Props) {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const slug = params.platformSlug ?? "";
  const route = PLATFORM_ROUTES.find((r) => r.slug === slug);

  usePageSEO(
    route ? `seo.platform.${route.platform}.title` : "meta.title",
    `/platforms/${slug}`,
  );

  if (!route) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">{t("not_found.heading")}</h1>
          <a href={lp("/platforms")} className="text-primary hover:underline">{t("seo.platform.index_h1")}</a>
        </div>
      </div>
    );
  }

  const platformKey = route.platform;
  const platformName = t(`seo.platform.${platformKey}.name`);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground">
              {t("nav.back")}
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
          <a href={lp("/")} className="hover:underline">{t("seo.common.home")}</a>
          <span className="mx-2">/</span>
          <a href={lp("/platforms")} className="hover:underline">{t("seo.platform.index_h1")}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{platformName}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {t(`seo.platform.${platformKey}.h1`)}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-8">
          {t(`seo.platform.${platformKey}.intro`)}
        </p>

        <div className="flex flex-wrap gap-3 mb-12">
          <a href={lp("/")}
             className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90">
            {t("seo.common.cta_pick_teams")}
          </a>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.platform.how_to_heading", { platform: platformName })}
          </h2>
          <ol className="space-y-4">
            {STEP_KEYS.map((step) => (
              <li key={step} className="flex gap-4">
                <span className="flex-none w-8 h-8 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-sm">
                  {step}
                </span>
                <p className="flex-1 text-sm text-muted-foreground pt-1">
                  {t(`seo.platform.${platformKey}.steps.${step}`)}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.platform.what_you_get_h2")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("seo.platform.what_you_get_body", { platform: platformName })}
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.platform.faq_h2")}
          </h2>
          <div className="space-y-4">
            {FAQ_KEYS.map((n) => (
              <div key={n}>
                <h3 className="text-sm font-semibold mb-1">
                  {t(`seo.platform.faq.q${n}`, { platform: platformName })}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(`seo.platform.faq.a${n}`, { platform: platformName })}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.platform.other_platforms")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {PLATFORM_ROUTES.filter((r) => r.slug !== slug).map((r) => (
              <a key={r.slug}
                 href={lp(`/platforms/${r.slug}`)}
                 className="block rounded-md border border-border/50 px-4 py-3 hover:bg-muted/30">
                <div className="font-medium">{t(`seo.platform.${r.platform}.name`)}</div>
                <div className="text-xs text-muted-foreground mt-1">{t(`seo.platform.${r.platform}.tagline`)}</div>
              </a>
            ))}
          </div>
        </section>
      </main>

      <MegaFooter />
    </div>
  );
}
